import { Injectable } from '@angular/core';
import { UserDto } from '../../../auth/user.dto';
import { RsjInstructionDto } from '../../../beneficiaries/rsj/rsj-instruction/dto/rsj-instruction.dto';
import { Clearances } from '../../models/enums/clearance.enum';
import { InstructionRsjTraceabilityStatus } from '../../traceability/instruction-rsj-traceability-status.enum';
import { TPermission } from '../permissions';
import { IPermissionService } from './permission.service.interface';

@Injectable({
  providedIn: 'root',
})
export class RsjInstructionPermissionService implements IPermissionService<RsjInstructionDto> {
  protected workflowAccess(user: UserDto): { [group: string]: { state: { [state: string]: TPermission[] } } } {
    return {
      ['ADMIN']: {
        state: {
          [InstructionRsjTraceabilityStatus.Creation]: [
            'RSJ_INSTRUCTION_CAN_SAVE',
            'RSJ_INSTRUCTION_CAN_SIGN',
            'RSJ_INSTRUCTION_CAN_SYNCHRONIZE',
            'RSJ_INSTRUCTION_CAN_DELETE',
          ],
          [InstructionRsjTraceabilityStatus.Analysis]: [
            'RSJ_INSTRUCTION_CAN_SAVE_DIE',
            'RSJ_INSTRUCTION_CAN_ASK_FOR_INFO',
            'RSJ_INSTRUCTION_CAN_ACCEPT',
            'RSJ_INSTRUCTION_CAN_SYNCHRONIZE',
            'RSJ_INSTRUCTION_CAN_REFUSE',
            'RSJ_INSTRUCTION_CAN_DELETE',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_REFUS_RENOUV',
            'RSJ_INSTRUCTION_DOC_REFUS_ELIGIBILITE',
          ],
          [InstructionRsjTraceabilityStatus.Refused]: [
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_REFUS_RENOUV',
            'RSJ_INSTRUCTION_DOC_REFUS_ELIGIBILITE',
            'RSJ_INSTRUCTION_CAN_DUPLICATE',
            'RSJ_INSTRUCTION_CAN_SAVE_DIE_COMMENT',
          ],
          [InstructionRsjTraceabilityStatus.WaitingForInfo]: [
            'RSJ_INSTRUCTION_CAN_SAVE',
            'RSJ_INSTRUCTION_CAN_VALIDATE',
            'RSJ_INSTRUCTION_CAN_ACCEPT',
            'RSJ_INSTRUCTION_CAN_SYNCHRONIZE',
            'RSJ_INSTRUCTION_CAN_DELETE',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_CAN_SAVE_DIE_COMMENT',
          ],
          [InstructionRsjTraceabilityStatus.Accepted]: [
            'RSJ_INSTRUCTION_CAN_ASK_SUSPENSION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
            'RSJ_INSTRUCTION_CAN_SAVE_DIE_COMMENT',
          ],
          [InstructionRsjTraceabilityStatus.PaymentInProgress]: [
            'RSJ_INSTRUCTION_CAN_ASK_SUSPENSION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
            'RSJ_INSTRUCTION_CAN_DUPLICATE',
            'RSJ_INSTRUCTION_CAN_SAVE_DIE_COMMENT',
          ],
          [InstructionRsjTraceabilityStatus.SuspensionInProgress]: [
            'RSJ_INSTRUCTION_CAN_SAVE_DIE',
            'RSJ_INSTRUCTION_CAN_ACCEPT_SUSPENSION',
            'RSJ_INSTRUCTION_CAN_REFUSE_SUSPENSION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
            'RSJ_INSTRUCTION_DOC_REPONSE_SUSPENSION',
          ],
          [InstructionRsjTraceabilityStatus.Suspended]: [
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
            'RSJ_INSTRUCTION_DOC_REPONSE_SUSPENSION',
            'RSJ_INSTRUCTION_CAN_DUPLICATE',
          ],
          [InstructionRsjTraceabilityStatus.WaitingForNonRenewal]: [
            'RSJ_INSTRUCTION_CAN_VALIDATE_NON_RENEWAL',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
          ],
          [InstructionRsjTraceabilityStatus.Done]: [
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
            'RSJ_INSTRUCTION_CAN_DUPLICATE',
          ],
          [InstructionRsjTraceabilityStatus.PaymentError]: [
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
          ],
        },
      },
      ['STRUCTURE']: {
        state: {
          [InstructionRsjTraceabilityStatus.Creation]: [
            'RSJ_INSTRUCTION_CAN_SAVE',
            'RSJ_INSTRUCTION_CAN_SIGN',
            'RSJ_INSTRUCTION_CAN_SYNCHRONIZE',
            'RSJ_INSTRUCTION_CAN_DELETE',
          ],
          [InstructionRsjTraceabilityStatus.Analysis]: ['RSJ_INSTRUCTION_CAN_DELETE', 'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION'],
          [InstructionRsjTraceabilityStatus.Refused]: ['RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION', 'RSJ_INSTRUCTION_CAN_DUPLICATE'],
          [InstructionRsjTraceabilityStatus.WaitingForInfo]: [
            'RSJ_INSTRUCTION_CAN_SAVE',
            'RSJ_INSTRUCTION_CAN_VALIDATE',
            'RSJ_INSTRUCTION_CAN_SYNCHRONIZE',
            'RSJ_INSTRUCTION_CAN_DELETE',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
          ],
          [InstructionRsjTraceabilityStatus.Accepted]: [
            'RSJ_INSTRUCTION_CAN_ASK_SUSPENSION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
          ],
          [InstructionRsjTraceabilityStatus.PaymentInProgress]: [
            'RSJ_INSTRUCTION_CAN_ASK_SUSPENSION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
            'RSJ_INSTRUCTION_CAN_DUPLICATE',
          ],
          [InstructionRsjTraceabilityStatus.SuspensionInProgress]: [
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
          ],
          [InstructionRsjTraceabilityStatus.Suspended]: [
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
            'RSJ_INSTRUCTION_CAN_DUPLICATE',
          ],
          [InstructionRsjTraceabilityStatus.WaitingForNonRenewal]: [
            'RSJ_INSTRUCTION_CAN_VALIDATE_NON_RENEWAL',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
          ],
          [InstructionRsjTraceabilityStatus.Done]: [
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
            'RSJ_INSTRUCTION_CAN_DUPLICATE',
          ],
          [InstructionRsjTraceabilityStatus.PaymentError]: [
            'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
            'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
            'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
            'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
            'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
          ],
        },
      },
      ['DIRECTION+']: {
        state: {
          [InstructionRsjTraceabilityStatus.Creation]: [],
          [InstructionRsjTraceabilityStatus.Analysis]: (user.clearances || []).some((value) => value === Clearances.CLR_VALIDATOR_RSJ)
            ? [
                'RSJ_INSTRUCTION_CAN_SAVE_DIE',
                'RSJ_INSTRUCTION_CAN_ASK_FOR_INFO',
                'RSJ_INSTRUCTION_CAN_ACCEPT',
                'RSJ_INSTRUCTION_CAN_SYNCHRONIZE',
                'RSJ_INSTRUCTION_CAN_REFUSE',
                'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
                'RSJ_INSTRUCTION_DOC_REFUS_RENOUV',
                'RSJ_INSTRUCTION_DOC_REFUS_ELIGIBILITE',
              ]
            : [],
          [InstructionRsjTraceabilityStatus.Refused]: (user.clearances || []).some((value) => value === Clearances.CLR_VALIDATOR_RSJ)
            ? [
                'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
                'RSJ_INSTRUCTION_DOC_REFUS_RENOUV',
                'RSJ_INSTRUCTION_DOC_REFUS_ELIGIBILITE',
                'RSJ_INSTRUCTION_CAN_SAVE_DIE_COMMENT',
              ]
            : [],
          [InstructionRsjTraceabilityStatus.WaitingForInfo]: (user.clearances || []).some((value) => value === Clearances.CLR_VALIDATOR_RSJ)
            ? [
                'RSJ_INSTRUCTION_CAN_REFUSE',
                'RSJ_INSTRUCTION_CAN_SAVE_DIE',
                'RSJ_INSTRUCTION_CAN_ACCEPT',
                'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
                'RSJ_INSTRUCTION_CAN_SAVE_DIE_COMMENT',
              ]
            : [],
          [InstructionRsjTraceabilityStatus.Accepted]: (user.clearances || []).some((value) => value === Clearances.CLR_VALIDATOR_RSJ)
            ? [
                'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
                'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
                'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
                'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
                'RSJ_INSTRUCTION_CAN_SAVE_DIE_COMMENT',
              ]
            : [],
          [InstructionRsjTraceabilityStatus.PaymentInProgress]: (user.clearances || []).some((value) => value === Clearances.CLR_VALIDATOR_RSJ)
            ? [
                'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
                'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
                'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
                'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
                'RSJ_INSTRUCTION_CAN_SAVE_DIE_COMMENT',
              ]
            : [],
          [InstructionRsjTraceabilityStatus.SuspensionInProgress]: (user.clearances || []).some((value) => value === Clearances.CLR_VALIDATOR_RSJ)
            ? [
                'RSJ_INSTRUCTION_CAN_SAVE_DIE',
                'RSJ_INSTRUCTION_CAN_ACCEPT_SUSPENSION',
                'RSJ_INSTRUCTION_CAN_REFUSE_SUSPENSION',
                'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
                'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
                'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
                'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
                'RSJ_INSTRUCTION_DOC_REPONSE_SUSPENSION',
              ]
            : [],
          [InstructionRsjTraceabilityStatus.Suspended]: (user.clearances || []).some((value) => value === Clearances.CLR_VALIDATOR_RSJ)
            ? [
                'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
                'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
                'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
                'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
                'RSJ_INSTRUCTION_DOC_REPONSE_SUSPENSION',
              ]
            : [],
          [InstructionRsjTraceabilityStatus.WaitingForNonRenewal]: (user.clearances || []).some((value) => value === Clearances.CLR_VALIDATOR_RSJ)
            ? [
                'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
                'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
                'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
                'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
              ]
            : [],
          [InstructionRsjTraceabilityStatus.Done]: (user.clearances || []).some((value) => value === Clearances.CLR_VALIDATOR_RSJ)
            ? [
                'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
                'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
                'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
                'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
              ]
            : [],
          [InstructionRsjTraceabilityStatus.PaymentError]: (user.clearances || []).some((value) => value === Clearances.CLR_VALIDATOR_RSJ)
            ? [
                'RSJ_INSTRUCTION_DOC_ATTESTATION_SITUATION',
                'RSJ_INSTRUCTION_DOC_ATTESTATION_RSJ',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_AGE',
                'RSJ_INSTRUCTION_DOC_FIN_DROIT_VERSEMENT',
                'RSJ_INSTRUCTION_DOC_OUVERTURE_DROIT',
                'RSJ_INSTRUCTION_DOC_RENOUV_APRES_SORTIE',
              ]
            : [],
        },
      },
    };
  }

  getPermissions(user: UserDto, subject: RsjInstructionDto): TPermission[] {
    return this.workflowAccess(user)[user.groups[0]]?.state[subject.status];
  }
}
